import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { GetPlanMoveQueryVariables } from 'src/graphql/generated/graphql'
import { useEffect, useMemo, useState } from 'src/hooks'
import { CheckoutQueryVariables } from 'src/saleor_graphql/generated/graphql'
import { queryKeyStore } from '../queryKeyStore'
import {
  addCheckoutLines,
  createCheckout,
  deleteCheckoutLines,
  getSaleorAccessToken,
  getSaleorRefreshToken,
  updateSaleorCustomer,
} from './fetching'

export const useGetEarnedBadges = () => {
  return useQuery(queryKeyStore.coachingBadges.list({ hasEarned: true }))
}

export const useGetInProgressBadges = () => {
  const query = useQuery(
    queryKeyStore.coachingBadges.list({ hasEarned: false }),
  )
  return query
}

export const useGetPlanMove = (
  variables: GetPlanMoveQueryVariables,
  shouldNotTrigger?: boolean,
) => {
  const planMoveDetail = queryKeyStore.plan_move.detail(variables)
  return useQuery({
    ...planMoveDetail,
    enabled: !!!shouldNotTrigger,
    refetchOnWindowFocus: false,
  })
}

export const useSaleorOIDCAuthorization = (
  isEnabled: boolean,
  codeChallenge: Promise<string>,
  redirectUri: string,
) => {
  const searchParams = useMemo(
    () => new URLSearchParams(window.location.search),
    [],
  )
  const code = searchParams.get('code') ?? ''

  useEffect(() => {
    ;(async () => {
      if (isEnabled && !code) {
        window.location.href = `${
          process.env.REACT_APP_API_URL
        }/oauth2/authorize?response_type=code&code_challenge=${await codeChallenge}&code_challenge_method=S256&client_id=${
          process.env.REACT_APP_SALEOR_OIDC_CLIENT_ID
        }&redirect_uri=${redirectUri}`
      }
    })()

    return () => {}
  }, [code, isEnabled, searchParams, redirectUri, codeChallenge])

  return code
}

type SaleorAuthentication = {
  access_token: string
  expires_in: number
  token_type: 'Bearer'
  refresh_token: string
  id_token: string
  expires_at: number
}

/**
 * Performs OIDC authentication, first directs user to /authenticate then redirects back to the redirect_uri with the authentication code.
 * Once the authentication code is present it passes it to the /token endpoint to retrieve the access_token that can then be used on all Saleor
 * requests. The access token is persisted in local storage, if the token is found the user won't be reauthenticated it will just be returned from LS.
 * If the token is close to expiring (1 hour), a refresh token will be used to obtain a new acccess_token.
 *
 * @param redirectUri the uri to redirect you to with the authentication code, this must be added to the OIDC server and match
 * @returns access token detials (type SaleorAuthentication)
 */
export const useSaleorAuth = (redirectUri: string) => {
  const SALEOR_ACCESS_TOKEN_KEY = 'saleor_access_token'
  const SALEOR_CODE_VERIFIER_KEY = 'saleor_oidc_code_verifier'

  const getSaleorCustFromLs = () => {
    const persistedSaleorAccessToken: string | null = localStorage.getItem(
      SALEOR_ACCESS_TOKEN_KEY,
    )

    const accessToken = persistedSaleorAccessToken
      ? ((JSON.parse(
          persistedSaleorAccessToken,
        ) as unknown) as SaleorAuthentication)
      : null

    return accessToken
  }

  const persistedSaleorCustomer = getSaleorCustFromLs()
  const shouldAuthorize = persistedSaleorCustomer
    ? Date.now() > persistedSaleorCustomer.expires_at
    : true

  async function base64URLEncode(buffer: Uint8Array): Promise<string> {
    const encoded = btoa(String.fromCharCode.apply(null, Array.from(buffer)))
    return encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
  }

  async function sha256(str: string): Promise<Uint8Array> {
    const encoder = new TextEncoder()
    const data = encoder.encode(str)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    return new Uint8Array(hashBuffer)
  }

  const generateCodeVerifier = async (): Promise<string> => {
    const randomBytes = new Uint8Array(32)
    crypto.getRandomValues(randomBytes)
    const verifier = await base64URLEncode(randomBytes)
    if (shouldAuthorize) {
      localStorage.setItem(SALEOR_CODE_VERIFIER_KEY, verifier)
    }
    return verifier
  }

  const codeVerifier = (async () => {
    return (
      localStorage.getItem(SALEOR_CODE_VERIFIER_KEY) ?? generateCodeVerifier()
    )
  })()

  const codeChallenge = (async () => {
    return base64URLEncode(await sha256(await codeVerifier))
  })()

  const authorizationCode = useSaleorOIDCAuthorization(
    shouldAuthorize,
    codeChallenge,
    redirectUri,
  )

  const [code, setCode] = useState<string | null>(authorizationCode)

  const clearQueryParams = () => {
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.delete('code')

    const newUrl = `${window.location.pathname}?${searchParams.toString()}`

    window.history.replaceState({}, '', newUrl)
  }

  // This query only fires if the saleor access token doesn't exist and an authorization code is present
  const accessTokenMutation = useMutation({
    mutationFn: (code: string) =>
      getSaleorAccessToken(code, codeVerifier, redirectUri),
    onSuccess: data => {
      if (data && data.success) {
        const expiresInMs = data.expires_in * 1000
        const expiresAt = Date.now() + expiresInMs
        localStorage.setItem(
          SALEOR_ACCESS_TOKEN_KEY,
          JSON.stringify({ ...data, expires_at: expiresAt }),
        )
        localStorage.removeItem(SALEOR_CODE_VERIFIER_KEY)
      }
    },
    onSettled: () => {
      clearQueryParams()
      setCode(null)
    },
  })

  const refreshTokenMutation = useMutation({
    mutationFn: (refreshToken: string) =>
      getSaleorRefreshToken(refreshToken, codeVerifier, redirectUri),
    onSettled: () => {
      clearQueryParams()
      setCode(null)
    },
    onSuccess: data => {
      if (data && data.success) {
        const expiresInMs = data.expires_in * 1000
        const expiresAt = Date.now() + expiresInMs
        localStorage.setItem(
          SALEOR_ACCESS_TOKEN_KEY,
          JSON.stringify({ ...data, expires_at: expiresAt }),
        )
        localStorage.removeItem(SALEOR_CODE_VERIFIER_KEY)
      }
    },
  })

  useEffect(() => {
    ;(async () => {
      if (code && !!!persistedSaleorCustomer) {
        await accessTokenMutation.mutateAsync(code)
      }
      if (persistedSaleorCustomer) {
        // Just refresh the token if the expire time is within 1 hour
        const ONE_HOUR = 3_600_000
        if (persistedSaleorCustomer.expires_at - Date.now() <= ONE_HOUR) {
          // TODO This kept returning invalid_grant, add support later
          // await refreshTokenMutation.mutateAsync(
          //   persistedSaleorCustomer.refresh_token,
          // )
        }
      }
    })()
    return () => {
      localStorage.removeItem(SALEOR_CODE_VERIFIER_KEY)
    }
  }, [code, accessTokenMutation, persistedSaleorCustomer, refreshTokenMutation])

  return getSaleorCustFromLs()
}

export const useCheckout = (variables: CheckoutQueryVariables) => {
  return useQuery({ ...queryKeyStore.checkout.detail(variables) })
}

export const useCreateCheckout = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: createCheckout,

    onSettled: data => {
      if (data?.errors && data.errors.length) {
        // TODO - How do we want to handle errors?
        throw Error('There was a problem creating the checkout')
      }
      if (data?.checkout) {
        // Manually write to the cache with the mutation response
        queryClient.setQueryData(
          queryKeyStore.checkout.detail({ id: data.checkout.id }).queryKey,
          data.checkout,
        )
      }
    },
  })
}

export const useAddCheckoutLines = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: addCheckoutLines,
    onSettled: (data, _err, variables) => {
      if (data?.errors && data.errors.length) {
        // TODO - How do we want to handle errors?
        throw Error('There was a problem creating the checkout')
      }
      if (data?.checkout) {
        queryClient.setQueryData(
          queryKeyStore.checkout.detail({ id: variables.id }).queryKey,
          data.checkout,
        )
      }
    },
  })
}

export const useSaleorCustomer = () => {
  return useQuery({
    ...queryKeyStore.saleor_customer.detail,
  })
}

export const useUpdateSaleorCustomer = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: updateSaleorCustomer,
    onSettled: data => {
      if (data?.errors && data.errors.length) {
        // TODO - How do we want to handle errors?
        throw Error('There was a problem creating the checkout')
      }
      if (data?.user) {
        queryClient.setQueryData(
          queryKeyStore.saleor_customer.detail.queryKey,
          data.user,
        )
      }
    },
  })
}

export const useDeleteCheckoutLines = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: deleteCheckoutLines,
    onSettled: (data, _err, variables) => {
      if (data?.errors && data.errors.length > 0) {
        throw Error('Could not delete checkout line')
      }

      if (data?.checkout) {
        queryClient.setQueryData(
          queryKeyStore.checkout.detail({ id: variables.id }).queryKey,
          data.checkout,
        )
      }
    },
  })
}
